import { UserStateService } from 'src/app/auth/services/user-state.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import {
  ADASTRA_CREDENTIALS,
  DOMO_CREDENTIALS,
} from 'src/app/settings/services/company-credential-providers';
import { CompanyIntegration } from './integrations.types';

export const CAP_ALERTS_REQUIRE_ROLE = 'ua sa da';

/** Environment gate for the CAP Alerts page: open in DEV and STAGE only. */
export const CAP_ALERTS_PAGE_REQUIRE_ROLE = '!PROD !BETA !TEST';

export function isCapAlertsPageEnabled(
  userStateService: UserStateService,
): boolean {
  return userStateService.hasPermission(CAP_ALERTS_PAGE_REQUIRE_ROLE);
}

/** Catalog metadata only — dialogs/services come from COMPANY_INTEGRATION_REGISTRY. */
export type CompanyIntegrationCatalogEntry = Omit<
  CompanyIntegration,
  'component' | 'enableComponent' | 'componentService'
>;

export const COMPANY_INTEGRATIONS: CompanyIntegrationCatalogEntry[] = [
  {
    id: 'cap-alerts',
    name: 'CAP Alerts',
    description:
      'Broadcast real-time emergency alerts and critical safety notifications across your displays.',
    configureMode: 'component',
    logo: 'integration-cap-alerts',
    enabledKey: null,
    featureName: FeaturesService.FEATURE_ALERTS,
    requireRole: CAP_ALERTS_REQUIRE_ROLE,
  },
  {
    id: 'fuse',
    name: 'Avocor FUSE',
    description:
      'Manage your Avocor hardware interfaces and sync interactive display settings seamlessly.',
    configureMode: 'fields',
    enabledKey: 'fuseEnabled',
    requireRole: 'ua sa',
    logo: 'integration-avocor-fuse',
    dialogHint: 'Enter your API Key found in your',
    helpUrl: 'https://fuse.avocor.com/settings/api_keys?page=1',
    helpLabel: 'XYTE Settings',
    fields: [
      {
        key: 'fuseApiKey',
        label: 'FUSE API Key',
        placeholder: 'API Key',
        secret: true,
      },
    ],
  },
  {
    id: 'jamf',
    name: 'JAMF Pro',
    description:
      'Connect your Apple device management workflows to track hardware status and deployment.',
    configureMode: 'fields',
    enabledKey: 'jamfEnabled',
    exclusiveWith: 'mosyle',
    requireRole: 'ua sa',
    logo: 'integration-jamf-pro',
    dialogHint: 'Enter your Jamf Pro credentials.',
    helpUrl:
      'https://help.risevision.com/hc/en-us/articles/24606283279252-How-do-I-enable-automatic-alerts-or-classroom-alerts-on-an-AppleTV',
    helpLabel: 'How to find your credentials',
    fields: [
      { key: 'jamfDomain', label: 'Jamf Pro server', type: 'url' },
      {
        key: 'jamfClientId',
        label: 'Client ID',
        placeholder: 'Enter client ID',
      },
      {
        key: 'jamfClientSecret',
        label: 'Client secret',
        placeholder: 'Enter client secret',
        secret: true,
      },
    ],
  },
  {
    id: 'mosyle',
    name: 'Mosyle',
    description:
      'Streamline MDM deployment and monitor device compliance directly within your presentations.',
    configureMode: 'fields',
    enabledKey: 'mosyleEnabled',
    exclusiveWith: 'jamf',
    requireRole: 'ua sa',
    logo: 'integration-mosyle',
    dialogHint: 'Enter your Mosyle credentials.',
    helpUrl:
      'https://help.risevision.com/hc/en-us/articles/28989490003092-Install-Rise-Player-on-Apple-TV-using-Mosyle',
    helpLabel: 'How to find your credentials',
    fields: [
      { key: 'mosyleEmail', label: 'Enter admin email', type: 'email' },
      { key: 'mosylePassword', label: 'Enter password', secret: true },
      { key: 'mosyleAccessToken', label: 'Enter access token', secret: true },
    ],
  },
  {
    id: 'domo',
    name: 'Domo',
    description:
      'Pull live business intelligence dashboards and data visualizations into your slides.',
    configureMode: 'fields',
    enabledKey: DOMO_CREDENTIALS.enabledKey,
    requireRole: 'ua sa',
    logo: 'integration-domo',
    dialogHint:
      'Connect to your Domo account to display the content. Rise Vision does not access, store, or view personal information or passwords that you may use to connect your accounts.',
    fields: [
      { key: DOMO_CREDENTIALS.clientIdKey, label: 'Enter your client ID' },
      {
        key: DOMO_CREDENTIALS.clientSecretKey,
        label: 'Enter your secret',
        secret: true,
      },
    ],
    featureName: FeaturesService.FEATURE_DOMO,
  },
  {
    id: 'adastra',
    name: 'Ad Astra',
    description:
      'Sync academic scheduling, room monitoring, and event data to your digital signage.',
    configureMode: 'fields',
    enabledKey: ADASTRA_CREDENTIALS.enabledKey,
    requireRole: 'ua sa',
    logo: 'integration-ad-astra',
    dialogTitle: 'Connect your Ad Astra Account',
    dialogHint:
      'Connect to your Ad Astra account to import your calendars in the EMS components.',
    fields: [
      {
        key: ADASTRA_CREDENTIALS.clientIdKey,
        label: 'Enter your client ID',
      },
      {
        key: ADASTRA_CREDENTIALS.clientSecretKey,
        label: 'Enter your secret',
        secret: true,
      },
    ],
    featureName: FeaturesService.FEATURE_EMS,
  },
];
